import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const KEYWORD_CATEGORY_OPTIONS = [
  "原木香｜品牌查詢",
  "原木香｜臥香線香",
  "原木香｜沉香檀香",
  "原木香｜香爐香具",
  "原木香｜精油香氛",
  "原木香｜天然蚊香",
  "原木香｜佛珠配件",
  "原木香｜購買情境",
  "未分類",
] as const;

export type KeywordCategoryOption = (typeof KEYWORD_CATEGORY_OPTIONS)[number];
export type KeywordBrandKey = "YMSWOOD";

const YMSWOOD_CATEGORY_ALIASES: Record<string, KeywordCategoryOption> = {
  原木香: "原木香｜品牌查詢",
  YMSWOOD: "原木香｜品牌查詢",
  ymswood: "原木香｜品牌查詢",
  臥香: "原木香｜臥香線香",
  線香: "原木香｜臥香線香",
  無黏香: "原木香｜臥香線香",
  小盤香: "原木香｜臥香線香",
  香塔: "原木香｜臥香線香",
  沉香: "原木香｜沉香檀香",
  檀香: "原木香｜沉香檀香",
  沉香粉: "原木香｜沉香檀香",
  檀香粉: "原木香｜沉香檀香",
  香爐: "原木香｜香爐香具",
  香具: "原木香｜香爐香具",
  香插: "原木香｜香爐香具",
  精油: "原木香｜精油香氛",
  香氛: "原木香｜精油香氛",
  蚊香: "原木香｜天然蚊香",
  天然蚊香: "原木香｜天然蚊香",
  佛珠: "原木香｜佛珠配件",
  手珠: "原木香｜佛珠配件",
  拜拜: "原木香｜購買情境",
  禮佛: "原木香｜購買情境",
  淨化: "原木香｜購買情境",
  未分類: "未分類",
};

export function normalizeKeywordCategory(category?: string | null, brand?: KeywordBrandKey): KeywordCategoryOption {
  const value = (category || "未分類").trim();
  if (KEYWORD_CATEGORY_OPTIONS.includes(value as KeywordCategoryOption)) {
    return value as KeywordCategoryOption;
  }
  if (brand === "YMSWOOD") return YMSWOOD_CATEGORY_ALIASES[value] || "未分類";
  return YMSWOOD_CATEGORY_ALIASES[value] || "未分類";
}

interface KeywordCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultKeyword?: string;
  defaultCategory?: string | null;
  defaultBrand?: KeywordBrandKey;
  onCreated?: (created: { keyword: string; category: string }) => void;
}

export default function KeywordCreateDialog({
  open,
  onOpenChange,
  defaultKeyword = "",
  defaultCategory = "未分類",
  defaultBrand,
  onCreated,
}: KeywordCreateDialogProps) {
  const [keyword, setKeyword] = useState(defaultKeyword);
  const [category, setCategory] = useState(normalizeKeywordCategory(defaultCategory, defaultBrand));
  const utils = trpc.useUtils();

  useEffect(() => {
    if (!open) return;
    setKeyword(defaultKeyword);
    setCategory(normalizeKeywordCategory(defaultCategory, defaultBrand));
  }, [defaultBrand, defaultCategory, defaultKeyword, open]);

  const createMutation = trpc.keywords.create.useMutation({
    onSuccess: () => {
      utils.keywords.getAll.invalidate();
      utils.keywords.getCategories.invalidate();
      toast.success("關鍵字已加入列表");
      onCreated?.({
        keyword: keyword.trim(),
        category,
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(`加入失敗：${error.message}`);
    },
  });

  const handleCreate = () => {
    if (!keyword.trim()) {
      toast.error("請輸入關鍵字");
      return;
    }

    createMutation.mutate({
      keyword: keyword.trim(),
      category,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>新增關鍵字</DialogTitle>
          <DialogDescription>
            新增要追蹤的關鍵字，系統將自動抓取趨勢數據
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="create-keyword">關鍵字 *</Label>
            <Input
              id="create-keyword"
              placeholder="例如：沉香臥香"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-category">分類</Label>
            <Select value={category} onValueChange={(value) => setCategory(normalizeKeywordCategory(value, defaultBrand))}>
              <SelectTrigger id="create-category">
                <SelectValue placeholder="選擇分類" />
              </SelectTrigger>
              <SelectContent>
                {KEYWORD_CATEGORY_OPTIONS.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? "新增中..." : "新增"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
